import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { useBase } from "./hooks";
import { nameOf } from "./model";
import { DemoNote } from "./parts";
import { LinkFormular, LinkListe } from "./KitLinks";
import { loadKits } from "./mediakit-daten";
import { t } from "../../../lib/sprache";

/**
 * Pitch- bzw. Castingliste (0074): mehrere Creator als ein Link für eine Marke (befristet, widerrufbar, mit Protokoll), dazu
 * alle Links der Agentur mit Stand und Aufrufen. Eingeschränkte Manager sehen und teilen nur ihre Creator.
 */
export function PitchListen() {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const { base, error, loading, noAccess, retry } = useBase(ws);
  const kits = useQuery({ queryKey: ["mediakit", orgId, "alle"], queryFn: () => loadKits(api, orgId) });
  // K-539: aus einer Anfrage (?anfrage=…&marke=…&kontakt=…&creator=a,b) mit Marke, Ansprechperson und Creatorn vorbelegt
  const [params] = useSearchParams();
  const uuid = (x: string | null) => (x && /^[0-9a-f-]{36}$/.test(x) ? x : null);
  const anfrage = uuid(params.get("anfrage"));
  const marke = uuid(params.get("marke"));
  const kontaktId = uuid(params.get("kontakt"));
  const kontakt = useQuery({
    queryKey: ["brand_contact", kontaktId], enabled: !!kontaktId,
    queryFn: async () => (await api.select<{ email: string | null }>("brand_contacts", { columns: "email", filters: [["id", "eq", kontaktId!]] }))[0]?.email ?? null,
  });
  const [wahl, setWahl] = useState<string[]>(() => (params.get("creator") ?? "").split(",").map(uuid).filter((x): x is string => !!x).slice(0, 30));
  const [zurAnfrage, setZurAnfrage] = useState(false);
  useEffect(() => { setZurAnfrage(false); }, [anfrage]);
  const [suche, setSuche] = useState("");
  const aktive = useMemo(() => (base?.talents ?? []).filter((t) => t.status !== "archived").sort((a, b) => nameOf(a).localeCompare(nameOf(b), "de")), [base]);
  if (error ?? kits.error) return <ErrorState error={error ?? kits.error} retry={() => { retry(); void kits.refetch(); }} />;
  if (noAccess) return <NoAccess />;
  if (loading || !base || kits.isPending || (kontaktId && kontakt.isPending)) return <Loading />;
  const gefiltert = aktive.filter((t) => !suche.trim() || nameOf(t).toLowerCase().includes(suche.trim().toLowerCase()));
  const brandName = (id: string | null) => (id ? base.brandById.get(id)?.name ?? null : null);
  const talentName = (id: string) => (base.talentById.get(id) ? nameOf(base.talentById.get(id)!) : "Creator");
  const toggle = (id: string, on: boolean) => setWahl((w) => (on ? [...new Set([...w, id])] : w.filter((x) => x !== id)));

  return (
    <main id="main" className="content tk-pitch">
      <div className="pagehead">
        <div>
          <div className="eyebrow"><Link to="/agentur/creator">Creator</Link> {t("· Pitch-Listen", "· Pitch lists")}</div>
          <h1>{t("Pitch-Listen und Media-Kits", "Pitch lists and media kits")}</h1>
          <p>{t("Mehrere Creator als ein persönlicher Link für eine Marke, mit Profil, Kennzahlen und auf Wunsch Referenzen und Preisen. Profile pflegst du im Media-Kit des jeweiligen Creators.", "Several creators as one personal link for a brand, with profile, metrics and optionally references and prices. You maintain profiles in each creator's media kit.")}</p>
        </div>
      </div>
      <section className="panel stack" aria-labelledby="pitch-auswahl">
        <h2 id="pitch-auswahl">{t("Creator auswählen", "Choose creators")}{wahl.length ? t(` · ${wahl.length} gewählt`, ` · ${wahl.length} selected`) : ""}</h2>
        <label>{t("Suchen", "Search")}<input type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Name" /></label>
        {gefiltert.length === 0 ? <p>{t("Kein Creator gefunden.", "No creator found.")}</p> : (
          <div className="grid tk-gap-sm">
            {gefiltert.map((tl) => (
              <label key={tl.id} className="row">
                <input type="checkbox" checked={wahl.includes(tl.id)} onChange={(e) => toggle(tl.id, e.target.checked)} />
                <span>{nameOf(tl)} <small>· <Link to={`/agentur/creator/${tl.id}/mediakit`}>{t("Media-Kit", "Media kit")}</Link></small></span>
              </label>
            ))}
          </div>
        )}
        {wahl.length > 30 && <p className="tk-error" role="alert">{t("Höchstens 30 Creator in einer Liste.", "At most 30 creators in one list.")}</p>}
      </section>
      {anfrage && (
        <p className="notice" role="status">
          {zurAnfrage
            ? <>{t("Die Pitch-Liste steht jetzt im Verlauf der Anfrage. ", "The pitch list is now in the inquiry's history. ")}<Link to={`/agentur/anfragen/${anfrage}`}>{t("Zur Anfrage", "Back to the inquiry")}</Link></>
            : <>{t("Aus der Anfrage vorbelegt: Marke, Ansprechperson und vorgeschlagene Creator. Nach dem Anlegen steht die Liste im Verlauf der Anfrage. ", "Prefilled from the inquiry: brand, contact and proposed creators. Once created, the list appears in the inquiry's history. ")}<Link to={`/agentur/anfragen/${anfrage}`}>{t("Zur Anfrage", "Back to the inquiry")}</Link></>}
        </p>
      )}
      <LinkFormular orgId={orgId} talentIds={wahl}
        options={{ brands: base.brands.map((b) => ({ id: b.id, name: b.name })).sort((a, b) => a.name.localeCompare(b.name, "de")), defaultLanguage: "de",
          vorbelegt: anfrage ? { brandId: marke, email: kontakt.data ?? null } : undefined }}
        onCreated={(kitId) => {
          void kits.refetch();
          if (anfrage) void api.rpc("inquiry_pitch_link", { p_inquiry: anfrage, p_kit: kitId }).then(() => setZurAnfrage(true)).catch(() => undefined);
        }}
        extra={wahl.length === 0 ? <p className="source-note">{t("Wähle oben mindestens einen Creator.", "Choose at least one creator above.")}</p> : <p className="source-note">{t("In der Liste: ", "In the list: ")}{wahl.map(talentName).join(", ")}.</p>} />
      <LinkListe kits={kits.data ?? []} talentName={talentName} brandName={brandName} onChanged={() => void kits.refetch()} empty={t("Noch keine Links an Marken.", "No links to brands yet.")} />
      <DemoNote />
    </main>
  );
}
